import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "./apiClient";

import cookiesUtility from "../utils/cookies.utility";

import { EtyUser } from "../interfaces/entities";
import { IChatAiResponse, IExpertConversationWithChatsForUser } from "../interfaces/expert_chats";
import { HMICreateExpertConversation, HMICreateExpertChat, HMIGenerateExpertChatAiResponse, HMIRegenerateExportChatAiResponseById, HMIAssignTitleToExpertConversation } from "../interfaces/user";


export const useGetUserDetails = () => {
    const queryFn = async (): Promise<EtyUser> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.get("/users/details");
        return response.data;
    };

    return useQuery({
        queryKey: ["user_details"],
        queryFn,
        staleTime: Infinity,
        retry: 0,
    });
};

export const useGetExpertConversations = () => {
    const queryFn = async (): Promise<IExpertConversationWithChatsForUser[]> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.get("/users/expert-conversations");
        return response.data;
    };

    return useQuery({
        queryKey: ["expert_conversations"],
        queryFn,
        retry: 0,
    });
};

// export const useGetExpertChatsByExpertConversationId = (studentId: number, expertConversationId: number) => {
//     const queryFn = async (): Promise<IExpertConversationWithChatsForUser> => {
//         const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
//         api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

//         const response = await api.get(`/users/student/${studentId}/expert-conversation/${expertConversationId}/expert-chats`);
//         return response.data;
//     };

//     return useQuery({
//         queryKey: ["expert_conversation_with_chats", studentId, expertConversationId],
//         queryFn,
//         staleTime: Infinity,
//         retry: 0,
//     });
// };

export const useCreateExpertConversation = () => {
    const mutationFn = async (variables: HMICreateExpertConversation) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.post(`/users/expert-conversation/${variables.student_id}`, variables.payload.create_expert_conversation);
        return response.data;
    };

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            const { setIsProcessing, setErrorMessage } = variables;
            setIsProcessing(true);
            setErrorMessage(null);
        },
        onSuccess: (data, variables) => {
            const { setIsProcessing, setErrorMessage, successCallback } = variables;
            setIsProcessing(false);
            setErrorMessage(null);
            if (data) {
                queryClient.invalidateQueries({ queryKey: ["expert_conversations"] });
                successCallback(data);
            }
        },
        onError: (error, variables) => {
            const { setIsProcessing, setErrorMessage } = variables;
            setIsProcessing(false);
            setErrorMessage(error.message);
        },
        onSettled: (_, __, variables) => {
            const { setIsProcessing, setErrorMessage } = variables;
            setIsProcessing(false);
            setErrorMessage(null);
        },
    });
};

export const useGetExpertConversationsWithChatsForStudent = (studentId: number) => {
    const queryFn = async (): Promise<IExpertConversationWithChatsForUser[]> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.get(`/users/student/${studentId}/expert-conversations`);
        return response.data;
    };

    return useQuery({
        queryKey: ["expert_conversations_with_chats_for_student", studentId],
        queryFn,
        retry: 0,
    });
};

export const useCreateExpertChat = () => {
    const mutationFn = async (variables: HMICreateExpertChat) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.post(`/users/expert-chat`, variables.payload.create_expert_chat);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            const { setIsProcessing, setErrorMessage } = variables;
            setIsProcessing(true);
            setErrorMessage(null);
        },
        onSuccess: (data, variables) => {
            const { setIsProcessing, setErrorMessage, successCallback } = variables;
            setIsProcessing(false);
            setErrorMessage(null);
            if (data) {
                successCallback(data);
            }
        },
        onError: (error, variables) => {
            const { setIsProcessing, setErrorMessage } = variables;
            setIsProcessing(false);
            setErrorMessage(error.message);
        },
        onSettled: (_, __, variables) => {
            const { setIsProcessing, setErrorMessage } = variables;
            setIsProcessing(false);
            setErrorMessage(null);
        },
    });
};

export const useGenerateExpertChatAiResponse = () => {
    const mutationFn = async (variables: HMIGenerateExpertChatAiResponse): Promise<IChatAiResponse[]> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.post(`/users/generate-ai-response/${variables.payload.expert_conversation_id}`);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            const { setIsProcessing, setErrorMessage } = variables;
            setIsProcessing(true);
            setErrorMessage(null);
        },
        onSuccess: (data, variables) => {
            const { setIsProcessing, setErrorMessage, successCallback } = variables;
            setIsProcessing(false);
            setErrorMessage(null);
            if (data) {
                successCallback(data);
            }
        },
        onError: (error, variables) => {
            const { setIsProcessing, setErrorMessage } = variables;
            setIsProcessing(false);
            setErrorMessage(error.message);
        },
        onSettled: (_, __, variables) => {
            const { setIsProcessing, setErrorMessage } = variables;
            setIsProcessing(false);
            setErrorMessage(null);
        },
    });
};

export const useRegenerateExpertChatAiResponseById = () => {
    const mutationFn = async (variables: HMIRegenerateExportChatAiResponseById) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.post(`/users/regenerate-ai-response/${variables.payload.chat_ai_response_id}`);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            const { setErrorMessage } = variables;
            setErrorMessage(null);
        },
        onSuccess: (data, variables) => {
            const { setErrorMessage, successCallback } = variables;
            setErrorMessage(null);
            if (data) {
                successCallback(data);
            };
        },
        onError: (error, variables) => {
            const { setErrorMessage } = variables;
            setErrorMessage(error.message);
        },
        onSettled: (_, __, variables) => {
            const { setErrorMessage } = variables;
            setErrorMessage(null);
        },
    });
};

export const useAssignTitleToExpertConversation = () => {
    const mutationFn = async (variables: HMIAssignTitleToExpertConversation) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.patch(`/users/expert-conversation/assign-title/${variables.payload.expert_conversation_id}`);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            const { setErrorMessage } = variables;
            setErrorMessage(null);
        },
        onSuccess: (data, variables) => {
            const { setErrorMessage, successCallback } = variables;
            setErrorMessage(null);
            if (data) {
                successCallback(data);
            }
        },
        onError: (error, variables) => {
            const { setErrorMessage } = variables;
            setErrorMessage(error.message);
        },
        onSettled: (_, __, variables) => {
            const { setErrorMessage } = variables;
            setErrorMessage(null);
        },
    });
};